import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';

// import * as moment from 'moment'; - added globally to prevent all locales being included
import { Subscription } from 'rxjs';

import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

import { ModalService } from '../../services/modal/modal.service';
import { ModalDatepickerComponent } from '../modal-datepicker/modal-datepicker.component';

@Component({
    selector: 'app-datepicker',
    templateUrl: './datepicker.component.html',
    styleUrls: ['./datepicker.component.scss']
})
export class DatepickerComponent implements OnDestroy, OnInit {

    @Input() date: NgbDateStruct;

    @Input() fromControl: FormControl;
    @Input() toControl: FormControl;

    @Input() minDate: NgbDateStruct;
    @Input() maxDate: NgbDateStruct;

    @Input() hideLabel = false;

    @Input() disableRightBorder: boolean;

    @Output() dateChange = new EventEmitter();

    disabled: boolean;

    private dateFormat: string = 'YYYY-MM-DD';

    private subscription = Subscription.EMPTY;

    constructor (private modal: ModalService) { }

    ngOnInit () {
        // we need to disable this instance if we have a fromControl with no value
        if (this.fromControl) {
            this.subscription = this.fromControl.valueChanges.subscribe((value: any) => {
                this.disabled = !value;
                if (this.disabled) {
                    // clear the value
                    this.date = null;
                    this.dateChange.emit({
                        value: null
                    });
                }
            });
            // set initial
            this.disabled = !this.fromControl.value;
        }
    }

    ngOnDestroy () {
        this.subscription.unsubscribe();
    }

    openDatepicker () {

        if (this.disabled) {
            return;
        }

        const modalRef = this.modal.open(ModalDatepickerComponent);

        const modalInstance = modalRef.componentInstance;

        // set defaults
        modalInstance.minDate = this.minDate;
        modalInstance.maxDate = this.maxDate;

        // pass data
        if (this.fromControl && this.fromControl.value) {
            modalInstance.minDate = this.returnMinDate(this.parseDate(this.fromControl.value), this.minDate);
        }

        if (this.toControl && this.toControl.value) {
            modalInstance.maxDate = this.returnMaxDate(this.parseDate(this.toControl.value), this.maxDate); // use value of toControl as the maxDate
        }

        modalRef.result.then((date: NgbDateStruct) => {
            this.date = date;
            this.dateChange.emit({
                value: date
            });
        })
        .catch(() => {
            // modal dismissed, do nothing
        });

    }

    /**
     *
     * Compare two dates and return whichever is GREATER
     *
     */
    private returnMinDate (date1: NgbDateStruct, date2: NgbDateStruct) {

        // add dates to array
        const dates = [{
            date: date1,
            valueOf: date1 ? moment(date1).valueOf() : -1
        }, {
            date: date2,
            valueOf: date2 ? moment(date2).valueOf() : -1
        }];

        // sort the array so the largest valueOf is first
        dates.sort((a, b) => {
            if (a.valueOf < b.valueOf) return 1;
            if (a.valueOf > b.valueOf) return -1;
            return 0;
        });

        // check we have at least one value
        const hasValue = !!date1 || !!date2;

        return hasValue ? dates[0].date : undefined;

    }

    /**
     *
     * Compare two dates and return whichever is LESSER
     *
     */
    private returnMaxDate (date1: NgbDateStruct, date2: NgbDateStruct) {

        // add dates to array
        const dates = [{
            date: date1,
            valueOf: date1 ? moment(date1).valueOf() : -1
        }, {
            date: date2,
            valueOf: date2 ? moment(date2).valueOf() : -1
        }];

        // sort the array so the lowest valueOf is first
        dates.sort((a, b) => {
            if (a.valueOf > b.valueOf) return 1;
            if (a.valueOf < b.valueOf) return -1;
            return 0;
        });

        // check we have at least one value
        const hasValue = !!date1 || !!date2;

        return hasValue ? dates[0].date : undefined;

    }

    private parseDate (date: string): NgbDateStruct {
        const asDate = moment(date, this.dateFormat);

        return {
            year: asDate.year(),
            month: asDate.month() + 1,
            day: asDate.date()
        };
    }
}
